import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { getCategoryLabel } from '../lib/catalogCategories';

function CatalogSelect({ id, label, value, options, onChange, labelInside = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);
  const selectedOption = options.find((option) => option.value === value) || options[0];

  useEffect(() => {
    const closeOnOutsidePress = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) setIsOpen(false);
    };

    document.addEventListener('mousedown', closeOnOutsidePress);
    return () => document.removeEventListener('mousedown', closeOnOutsidePress);
  }, []);

  const handleTriggerKeyDown = (event) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      return;
    }

    if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      setIsOpen(true);
    }
  };

  return (
    <div className="shop-select-control" ref={selectRef}>
      <span className={labelInside ? 'visually-hidden' : 'shop-select-label'} id={`${id}-label`}>{label}</span>
      <div className="shop-select-field">
        <button
          id={id}
          type="button"
          className="shop-select-trigger"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-labelledby={`${id}-label ${id}`}
          onClick={() => setIsOpen((open) => !open)}
          onKeyDown={handleTriggerKeyDown}
        >
          <span className="shop-select-trigger-value">
            {labelInside && <span className="shop-select-prefix">{label} by: </span>}
            {selectedOption.label}
          </span>
          <span className="shop-select-caret" aria-hidden="true" />
        </button>

        {isOpen && (
          <div className="shop-select-menu" role="listbox" aria-labelledby={`${id}-label`}>
            {options.map((option) => (
              <button
                key={option.value || 'all'}
                type="button"
                role="option"
                aria-selected={option.value === value}
                className={option.value === value ? 'is-selected' : ''}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [loadAttempt, setLoadAttempt] = useState(0);

  const selectedCategory = searchParams.get('category') || '';
  const searchQuery = searchParams.get('search') || '';
  const selectedSort = searchParams.get('sort') || 'featured';
  const selectedBadge = searchParams.get('badge') || '';
  const selectedCategoryName = categories.find((category) => category.slug === selectedCategory)?.name;

  useEffect(() => {
    const controller = new AbortController();
    async function loadShopData() {
      setLoading(true);
      setLoadError('');
      try {
        const queryParams = new URLSearchParams();
        if (selectedCategory) queryParams.append('category', selectedCategory);
        if (searchQuery) queryParams.append('search', searchQuery);
        if (selectedSort) queryParams.append('sort', selectedSort);
        if (selectedBadge) queryParams.append('badge', selectedBadge);

        const [prodRes, catRes] = await Promise.all([
          fetch(`/api/products?${queryParams.toString()}`, { signal: controller.signal }),
          fetch('/api/categories', { signal: controller.signal }),
        ]);

        if (!prodRes.ok || !catRes.ok) {
          throw new Error('Catalog data could not be loaded.');
        }

        const prodData = await prodRes.json();
        const catData = await catRes.json();

        setProducts(Array.isArray(prodData) ? prodData : []);
        setCategories(Array.isArray(catData) ? catData : []);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Failed to load shop data:', err);
          setLoadError('We could not load the catalog. Check your connection and try again.');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }
    loadShopData();
    return () => controller.abort();
  }, [selectedCategory, searchQuery, selectedSort, selectedBadge, loadAttempt]);

  const updateFilter = (key, value) => {
    const nextParams = new URLSearchParams(searchParams);
    if (value) {
      nextParams.set(key, value);
    } else {
      nextParams.delete(key);
    }
    setSearchParams(nextParams);
  };

  return (
    <main className="shop-page container py-4 py-md-5">
      <section className="shop-toolbar" aria-label="Catalog filters">
        <div className="shop-toolbar-heading">
          <span>{selectedCategoryName || 'All handmade pieces'}</span>
          <strong aria-live="polite">{loading ? 'Loading products…' : `${products.length} available`}</strong>
        </div>
        <div className="shop-toolbar-controls">
          <div className="shop-filter-control">
            <CatalogSelect
              id="shop-filter"
              label="Filter"
              value={selectedBadge}
              options={[
                { value: '', label: 'All items' },
                { value: 'featured', label: 'Featured' },
                { value: 'new', label: 'New arrivals' },
                { value: 'bestseller', label: 'Bestsellers' },
              ]}
              onChange={(value) => updateFilter('badge', value)}
            />
          </div>
          <div className="shop-sort-control">
            <CatalogSelect
              id="shop-sort"
              label="Sort"
              labelInside
              value={selectedSort}
              options={[
                { value: 'featured', label: 'Featured' },
                { value: 'newest', label: 'Newest Arrivals' },
                { value: 'price_asc', label: 'Price: Low to High' },
                { value: 'price_desc', label: 'Price: High to Low' },
                { value: 'bestseller', label: 'Best Sellers' },
              ]}
              onChange={(value) => updateFilter('sort', value)}
            />
          </div>
        </div>
      </section>

      <nav className="shop-category-list" aria-label="Product categories">
        {categories.map((cat) => (
          <button
            key={cat.id}
            type="button"
            className={selectedCategory === cat.slug ? 'is-active' : ''}
            onClick={() => updateFilter('category', cat.slug)}
          >
            {getCategoryLabel(cat)}
          </button>
        ))}

        {searchQuery && (
          <span className="shop-search-chip">
            Search: "{searchQuery}"
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => updateFilter('search', '')}
            >×</button>
          </span>
        )}
      </nav>

      {loading ? (
        <div className="shop-loading-grid" aria-label="Loading products" aria-busy="true">
          {[1, 2, 3, 4].map((item) => <div key={item} className="shop-skeleton" />)}
        </div>
      ) : loadError ? (
        <div className="storefront-error" role="alert">
          <h2 className="brand-font">Catalog unavailable</h2>
          <p>{loadError}</p>
          <button type="button" className="btn btn-primary-custom" onClick={() => setLoadAttempt((attempt) => attempt + 1)}>
            Try again
          </button>
        </div>
      ) : products.length === 0 ? (
        <div className="shop-empty-state">
          <div className="fs-1 mb-2">🔍</div>
          <h5>No products found</h5>
          <p>Try another collection or clear the search.</p>

          <button className="btn btn-primary-custom" onClick={() => setSearchParams({})}>
            Reset All Filters
          </button>
        </div>
      ) : (
        <div className={`shop-products-grid ${products.length === 1 && !selectedCategory ? 'shop-products-grid--single' : ''} ${selectedCategory ? 'shop-products-grid--category' : ''}`}>
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      )}
    </main>
  );
}
